import { FileSearchOutlined, StopOutlined } from '@ant-design/icons'
import { Typography, Button, Table, Switch, message } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import {
  useUnreviewedReportsPagination,
  useUnreviewedReports,
  useFetchUnreviewedReports,
  updateReportPriority,
  unassignReport,
} from '../../redux/slices/unreviewed-reports'
import Filters from '../Forms/Filters'
import { toPath } from '../../routes'
import { formatDOB } from '../../lib/datetime'
import { useCurrentUserRole } from '../../redux/slices/session'
import { useAsync } from '../../hooks'
import { assignUnreviewed } from '../../api/reports'
import { PageHeader } from '../Common'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import useQueryParams from '../../hooks/useQueryParams'
import { reportShowPath, reportEditPath } from '../../lib/reports'
import AssignedReportDetails from './AssignedReportDetails'

const { Text } = Typography

const useStyles = createUseStyles({
  center: {
    textAlign: 'center',
  },
  ok: {
    color: 'green',
  },
  notOk: {
    color: 'red',
  },
})

const QUERY_PARAMS_SCHEMA = {
  page: Number,
}

const COLUMNS = [
  {
    title: 'Patient',
    dataIndex: ['patient', 'lastName'],
    render(text, report) {
      return (
        <Link to={toPath.patients.patient({ id: report.patient.id })}>
          {report.patient.firstName} {report.patient.lastName}
        </Link>
      )
    },
  },
  {
    title: 'DOB',
    dataIndex: ['patient', 'birthDate'],
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'typeName'],
  },
  {
    title: 'ICM Active',
    key: 'icmActive',
    align: 'center',
    render(text, report) {
      const { device } = report
      if (!device?.icmCapable) return '—'
      return (
        <CircleCheckIcon
          checked={device.icmActive}
          trueClassName="ok"
          falseClassName="notOk"
        />
      )
    },
  },
  {
    title: 'Cycle',
    dataIndex: 'cycleEndsAt',
    render(text) {
      const endDate = text.split('T').shift()

      return dayjs(endDate).format('MM/DD/YYYY')
    },
  },
  {
    title: 'Practice',
    dataIndex: 'practiceName',
  },
  {
    title: 'Assignee',
    dataIndex: ['report', 'assignee', 'id'],
    render(text, report) {
      return (
        report.assignee && (
          <Text>
            {report.assignee.firstName} {report.assignee.lastName}
          </Text>
        )
      )
    },
  },
]

function AdminView() {
  const classes = useStyles()

  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1 },
    schema: QUERY_PARAMS_SCHEMA,
  })
  const { pending: loading } = useAsync(
    useFetchUnreviewedReports(conditions),
    true,
  )
  const reports = useUnreviewedReports()

  const dispatch = useDispatch()
  const { currentPage, pageSize, total } = useUnreviewedReportsPagination()

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const handlePriorityChange = useCallback(
    async (record, priority) => {
      try {
        await dispatch(updateReportPriority(record, { priority }))
        message.success('Report successfully updated')
      } catch (e) {
        message.error()
      }
    },
    [dispatch],
  )
  const handleSubmit = useCallback(
    ({ practiceId, query, serialNumber }) => {
      setConditions({
        page: 1,
        practiceId,
        query,
        serialNumber,
      })
    },
    [setConditions],
  )

  const freeReport = useCallback(
    async record => {
      try {
        await dispatch(unassignReport(record))
        message.success('Report successfully unassigned')
      } catch (e) {
        message.error('There was an error unassigning the report')
      }
    },
    [dispatch],
  )

  const reportViewLink = record => (
    <Link
      to={{
        pathname: reportShowPath(record),
        search: 'unreviewed=true',
      }}
    >
      <Button type="link" icon={<FileSearchOutlined />} />
    </Link>
  )

  const columns = [
    ...COLUMNS,
    {
      title: 'Priority Report?',
      dataIndex: 'priority',
      render(_text, record) {
        return (
          <Switch
            checked={record.priority}
            checkedChildren="Priority"
            unCheckedChildren="Not&nbsp;Priority"
            onChange={priority => handlePriorityChange(record, priority)}
          />
        )
      },
    },
    {
      title: 'View',
      key: 'view',
      render(_text, record) {
        return <span>{reportViewLink(record)}</span>
      },
    },
    {
      title: 'Unassign',
      key: 'unassign',
      render(_text, record) {
        return (
          record.assignee && (
            <div className={classes.center}>
              <Button
                icon={<StopOutlined />}
                type="link"
                onClick={() => freeReport(record)}
              />
            </div>
          )
        )
      },
    },
  ]

  return (
    <div>
      <Filters
        showSearch
        showPractice
        onSubmit={handleSubmit}
        currentValues={conditions}
        placeholders={{ query: 'Search by name or device serial number' }}
      />
      <Table
        columns={columns}
        loading={loading}
        pagination={{
          onChange: handlePageChange,
          current: currentPage,
          pageSize,
          total,
        }}
        dataSource={reports}
        rowKey="id"
      />
    </div>
  )
}

function ClinicianView() {
  const { total } = useUnreviewedReportsPagination()
  const { pending: loading } = useAsync(useFetchUnreviewedReports(), true)
  const [isAssigning, setAssigning] = useState(false)
  const navigate = useNavigate()

  const reports = useUnreviewedReports()
  const report = reports.length > 0 ? reports[0] : null

  const assignReport = async () => {
    try {
      setAssigning(true)
      const response = await assignUnreviewed()
      navigate(reportEditPath(response))
    } catch (e) {
      message.error('There was an error assigning a report for review')
    } finally {
      setAssigning(false)
    }
  }

  if (reports.length === 0) {
    return (
      <div>
        <Typography.Title level={4}>
          There are {total} unreviewed reports
        </Typography.Title>
        <Button
          type="primary"
          disabled={loading || isAssigning || total === 0}
          onClick={assignReport}
        >
          Take one to review
        </Button>
      </div>
    )
  }

  return <AssignedReportDetails report={report} />
}

export default function UnreviewedReports() {
  const userRole = useCurrentUserRole()
  const isClinician = userRole === 'clinician'
  const isAdmin = userRole === 'admin'

  return (
    <>
      <PageHeader title="Unreviewed Reports" />
      {isAdmin && <AdminView />}
      {isClinician && <ClinicianView />}
    </>
  )
}
